import type { Account, Assumptions } from '../types';
import { deriveRequiredSpend, sumBucket, type ProjectionResult } from './projection';

export interface DashboardStats {
  currentAge: number;
  targetRetirementAge: number;
  currentNetWorth: number;
  currentInvestable: number;
  annualSavingsRate: number;
  currentAnnualSpend: number;
  // At-retirement projection
  projectedNestEgg: number;
  safeAnnualWithdrawal: number;
  requiredSpend: number;
  annualSurplus: number;
  requiredNestEgg: number;
  cushion: number;
  onTrack: boolean;
  // Rules of thumb
  costOf1TodayAtRetirement: number; // how many $ of future nest egg a $1 spent today "costs"
  costOf1TodayIncome: number; // income forever per $1 spent today
  mortgagePayoffYear: number | null;
}

export function computeDashboard(opts: {
  assumptions: Assumptions;
  accounts: Account[];
  currentBalances: Record<string, number>;
  projection: ProjectionResult;
}): DashboardStats {
  const { assumptions, accounts, currentBalances, projection } = opts;
  const yearsToRetirement = assumptions.targetRetirementAge - assumptions.currentAge;
  const retireIdx = Math.max(0, Math.min(yearsToRetirement, projection.years.length - 1));
  const atRetirement = projection.years[retireIdx];

  const hysa = sumBucket(accounts, currentBalances, 'hysa');
  const checking = accounts
    .filter(a => a.kind === 'checking' && !a.archived)
    .reduce((s, a) => s + (currentBalances[a.id] ?? 0), 0);
  const retirement = sumBucket(accounts, currentBalances, 'retirement');
  const taxable = sumBucket(accounts, currentBalances, 'taxable');
  const home = accounts
    .filter(a => a.kind === 'home' && !a.archived)
    .reduce((s, a) => s + (currentBalances[a.id] ?? 0), 0);
  const mortgage = accounts
    .filter(a => a.kind === 'mortgage' && !a.archived)
    .reduce((s, a) => s + (currentBalances[a.id] ?? 0), 0);
  const otherLiab = accounts
    .filter(a => a.kind === 'other_liability' && !a.archived)
    .reduce((s, a) => s + (currentBalances[a.id] ?? 0), 0);
  const otherAsset = accounts
    .filter(a => a.kind === 'other_asset' && !a.archived)
    .reduce((s, a) => s + (currentBalances[a.id] ?? 0), 0);

  // `currentInvestable` is "money working for me long-term" — checking is
  // transactional float and is excluded so this matches the projection
  // engine's `investable` (which sums only the hysa/retirement/taxable
  // buckets — checking has `bucket: null`). Net worth still counts checking.
  const currentInvestable = hysa + retirement + taxable + otherAsset;
  const currentNetWorth = currentInvestable + checking + home - mortgage - otherLiab;

  const requiredSpend = deriveRequiredSpend(assumptions);
  const requiredNestEgg = requiredSpend / assumptions.swr;

  const projectedNestEgg = atRetirement.investable;
  const safeAnnualWithdrawal = atRetirement.swrIncome;
  const annualSurplus = safeAnnualWithdrawal - requiredSpend;
  const onTrack = safeAnnualWithdrawal >= requiredSpend;
  const cushion = projectedNestEgg - requiredNestEgg;

  const annualSavingsRate =
    assumptions.annualContribution401k + assumptions.annualContributionTaxable + assumptions.annualContributionHysa;
  const currentAnnualSpend = assumptions.monthlyHouseholdSpend * 12;

  const costOf1TodayAtRetirement = Math.pow(1 + assumptions.stockRealReturn, yearsToRetirement);
  const costOf1TodayIncome = costOf1TodayAtRetirement * assumptions.swr;

  const payoff = projection.mortgage.find(m => m.endBalance <= 0);
  const mortgagePayoffYear = payoff ? payoff.calendarYear : null;

  return {
    currentAge: assumptions.currentAge,
    targetRetirementAge: assumptions.targetRetirementAge,
    currentNetWorth,
    currentInvestable,
    annualSavingsRate,
    currentAnnualSpend,
    projectedNestEgg,
    safeAnnualWithdrawal,
    requiredSpend,
    annualSurplus,
    requiredNestEgg,
    cushion,
    onTrack,
    costOf1TodayAtRetirement,
    costOf1TodayIncome,
    mortgagePayoffYear,
  };
}
